/**
 * Bot exclusion + "human heartbeat" gate (P4, Oct 2026).
 *
 *  - isBotClient(): isbot UA list + navigator.webdriver. Bots never load
 *    Clarity, never fire Meta Pixel events, never beacon our analytics.
 *  - whenHuman(): resolves ONCE per page load after 2s of visible dwell OR
 *    the first real interaction (pointer / key / scroll / touch / wheel),
 *    whichever comes first. Our own pageview beacon + Clarity wait on it so
 *    sub-2s drive-bys with zero interaction are never counted.
 */
import { isbot } from "isbot";

const DWELL_MS = 2000;
const INTERACTION_EVENTS = ["pointerdown", "keydown", "scroll", "touchstart", "wheel"];

let botCache = null;

export function isBotClient() {
  if (typeof navigator === "undefined") return false;
  if (botCache !== null) return botCache;
  try {
    const ua = navigator.userAgent || "";
    botCache = !ua || navigator.webdriver === true || isbot(ua);
  } catch {
    botCache = false;
  }
  return botCache;
}

let humanPromise = null;
let humanSignal = null;

/** "dwell" | "interaction" | null once confirmed. */
export function humanConfirmedBy() {
  return humanSignal;
}

export function whenHuman() {
  if (humanPromise) return humanPromise;
  humanPromise = new Promise((resolve) => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    let timer = null;
    const onInteract = () => finish("interaction");
    const onVisibility = () => {
      if (document.visibilityState === "visible") armTimer();
      else clearTimeout(timer);
    };
    const cleanup = () => {
      clearTimeout(timer);
      INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, onInteract, true));
      document.removeEventListener("visibilitychange", onVisibility);
    };
    const finish = (signal) => {
      if (humanSignal) return;
      humanSignal = signal;
      cleanup();
      resolve(signal);
    };
    const armTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (document.visibilityState === "visible") finish("dwell");
      }, DWELL_MS);
    };
    INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, onInteract, { passive: true, capture: true }));
    document.addEventListener("visibilitychange", onVisibility);
    if (document.visibilityState === "visible") armTimer();
  });
  return humanPromise;
}
